/**
 * Shared occurrence projection for scheduled items (Actions and recurring
 * Budgets). Single source of truth replacing the divergent copies that
 * previously lived in Dashboard.js, UpcomingTransactions.js, and
 * ActionMatrixView.js (see docs/ACTION-SCHEDULING-DESIGN.md).
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Local-midnight Date from a Date or parseable string; null if invalid.
const toLocalDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Project every occurrence of a scheduled item inside a date window.
 *
 * @param {Object} item
 * @param {Date|string} item.startDate - schedule anchor (required)
 * @param {Date|string} [item.endDate] - schedule stops here
 * @param {string} [item.frequency] - once|daily|weekly|monthly|quarterly|yearly;
 *   budgets may supply recurringPeriod instead
 * @param {string} [item.recurringPeriod] - fallback when frequency is absent
 * @param {number} [item.interval=1] - every N frequency units
 *   (weekly x2 = fortnightly, monthly x3 = quarterly)
 * @param {Date|string} windowStart - inclusive
 * @param {Date|string} windowEnd - inclusive
 * @returns {Date[]} occurrence dates at local midnight, ascending
 *
 * Month-anchored frequencies (monthly/quarterly/yearly) keep the start
 * date's day-of-month, clamped to the target month's length (an anchor on
 * the 31st lands on Feb 28 but returns to the 31st in March).
 */
export const generateOccurrences = (item, windowStart, windowEnd) => {
  if (!item) return [];
  const anchor = toLocalDay(item.startDate);
  const winStart = toLocalDay(windowStart);
  const winEnd = toLocalDay(windowEnd);
  if (!anchor || !winStart || !winEnd || winEnd < winStart) return [];

  const frequency = item.frequency || item.recurringPeriod || 'once';
  const rawInterval = Number(item.interval);
  const interval = Number.isFinite(rawInterval) && rawInterval >= 1 ? Math.floor(rawInterval) : 1;

  if (frequency === 'once') {
    return anchor >= winStart && anchor <= winEnd ? [anchor] : [];
  }

  const hardEnd = toLocalDay(item.endDate);
  const limit = hardEnd && hardEnd < winEnd ? hardEnd : winEnd;
  if (anchor > limit) return [];

  const dates = [];

  // Unknown frequencies fall through to monthly, matching the historical
  // default in the Dashboard projection.
  const monthsPerStep =
    frequency === 'yearly' ? 12 * interval :
    frequency === 'quarterly' ? 3 * interval :
    frequency === 'daily' || frequency === 'weekly' ? 0 :
    interval;

  if (monthsPerStep > 0) {
    // Compute occurrence n directly from the anchor so day-of-month never
    // drifts after a clamped (short) month.
    for (let n = 0; n < 5000; n++) {
      const totalMonths = anchor.getMonth() + n * monthsPerStep;
      const year = anchor.getFullYear() + Math.floor(totalMonths / 12);
      const month = totalMonths % 12;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const d = new Date(year, month, Math.min(anchor.getDate(), lastDay));
      if (d > limit) break;
      if (d >= winStart) dates.push(d);
    }
    return dates;
  }

  const stepDays = (frequency === 'weekly' ? 7 : 1) * interval;
  const cur = new Date(anchor);
  if (cur < winStart) {
    // Jump close to the window in one hop, then settle with single steps
    // (the hop uses ms math, so DST hours can leave it a step early).
    const stepsBehind = Math.floor((winStart - cur) / (stepDays * MS_PER_DAY));
    cur.setDate(cur.getDate() + stepsBehind * stepDays);
    while (cur < winStart) cur.setDate(cur.getDate() + stepDays);
  }
  while (cur <= limit && dates.length < 5000) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + stepDays);
  }
  return dates;
};

/**
 * Human label for a schedule's cadence, interval-aware.
 * cadenceLabel('weekly', 2) === 'Fortnightly'; ('monthly', 3) === 'Quarterly'.
 */
export const cadenceLabel = (frequency, interval = 1) => {
  const n = Number.isFinite(Number(interval)) && Number(interval) >= 1 ? Math.floor(Number(interval)) : 1;
  if (!frequency || frequency === 'once') return 'One-time';
  const base = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
  if (n === 1) return base[frequency] || frequency;
  if (frequency === 'weekly' && n === 2) return 'Fortnightly';
  if (frequency === 'monthly' && n === 3) return 'Quarterly';
  const unit = { daily: 'days', weekly: 'weeks', monthly: 'months', quarterly: 'quarters', yearly: 'years' }[frequency];
  return unit ? `Every ${n} ${unit}` : frequency;
};
